/**
 * GlobalTimer — persistent active-timer indicator in the Header.
 * Polls for a running draft timesheet every 30s.
 * Shows elapsed time with a pulsing dot. Click → stop the timer.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../provider/AuthProvider';
import { useInstalledApps } from '../provider/InstalledAppsProvider';
import { visibleInterval } from '../utils/visibleInterval';

function pad(n) { return String(Math.floor(n)).padStart(2, '0'); }

function formatElapsed(startedAt) {
  const secs = Math.floor((Date.now() - new Date(startedAt)) / 1000);
  const h    = Math.floor(secs / 3600);
  const m    = Math.floor((secs % 3600) / 60);
  const s    = secs % 60;
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export default function GlobalTimer() {
  const { token } = useAuth();
  const { isAppInstalled, isAppsLoading } = useInstalledApps() || {};
  // Timesheets belong to Projects: without it the endpoint answers 403 module_not_installed, which filled
  // Monitoring with a blocked request every 30 seconds.
  const projectsInstalled = !isAppsLoading && !!isAppInstalled?.('projects');
  const navigate  = useNavigate();
  const [entry,    setEntry]    = useState(null);   // running timer entry
  const [elapsed,  setElapsed]  = useState('');
  const [stopping, setStopping] = useState(false);
  const [open,     setOpen]     = useState(false);
  const pollRef  = useRef(null);
  const tickRef  = useRef(null);
  const panelRef = useRef(null);

  // ── Fetch running timer ───────────────────────────────────────────────────
  const poll = useCallback(async () => {
    if (!token || !projectsInstalled) return;
    try {
      const r = await api.get('/projects/timesheets/', {
        params: { draft: true, source: 'timer', page_size: 1 },
      });
      const entries = r.data?.results || r.data || [];
      const running = entries.find(e => e.timer_started_at && !e.hours);
      setEntry(running || null);
    } catch { setEntry(null); }
  }, [token, projectsInstalled]);

  useEffect(() => {
    if (!projectsInstalled) { setEntry(null); return undefined; }
    poll();
    pollRef.current = visibleInterval(poll, 30000);
    return () => clearInterval(pollRef.current);
  }, [poll]);

  // ── Tick elapsed display ──────────────────────────────────────────────────
  useEffect(() => {
    clearInterval(tickRef.current);
    if (entry?.timer_started_at) {
      const tick = () => setElapsed(formatElapsed(entry.timer_started_at));
      tick();
      tickRef.current = setInterval(tick, 1000);
    } else {
      setElapsed('');
    }
    return () => clearInterval(tickRef.current);
  }, [entry]);

  // ── Close panel on outside click ──────────────────────────────────────────
  useEffect(() => {
    const h = e => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Stop timer ────────────────────────────────────────────────────────────
  const stopTimer = async () => {
    if (!entry) return;
    setStopping(true);
    try {
      await api.patch(`/projects/timesheets/${entry.id}/stop-timer/`);
      setEntry(null);
      setOpen(false);
      await poll(); // refresh
    } catch(e) { console.error(e); }
    finally { setStopping(false); }
  };

  if (!entry) return null; // hidden when no timer running

  return (
    <div className="relative" ref={panelRef}>
      {/* ── Pill button ── */}
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[color:light-dark(#bfdbfe,#113462)] text-[#1d4ed8] bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer font-semibold text-[12px]"
        title="Active timer — click to manage"
      >
        {/* Pulsing dot */}
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
        </span>
        <span className="font-mono tracking-wider">{elapsed}</span>
        <span className="opacity-60 text-xs">▾</span>
      </button>

      {/* ── Dropdown panel ── */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          width: 280, background: 'var(--cx-surface)',
          border: '1.5px solid var(--cx-line)', borderRadius: 14,
          boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
          zIndex: 9999, overflow: 'hidden',
          fontFamily: "'Manrope', sans-serif",
          animation: 'timerSlideDown 0.15s ease',
        }}>
          <style>{`@keyframes timerSlideDown { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }`}</style>

          {/* Header */}
          <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid light-dark(#f1f5f9, #2a2e36)', display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:14, fontWeight:700, color:'var(--cx-ink)' }}>⏱ Active Timer</span>
            <div style={{ marginLeft:'auto', fontFamily:'monospace', fontSize:20, fontWeight:900, color:'#3b82f6', letterSpacing:2 }}>
              {elapsed}
            </div>
          </div>

          {/* Task info */}
          <div style={{ padding: '12px 16px' }}>
            {entry.task_title && (
              <div style={{ fontSize:13, fontWeight:700, color:'var(--cx-text-2)', marginBottom:4, lineHeight:1.3 }}>
                📋 {entry.task_title}
              </div>
            )}
            {entry.project_name && (
              <div style={{ fontSize: 12, color:'var(--cx-text-4)', marginBottom:8 }}>
                📁 {entry.project_name}
              </div>
            )}
            {entry.description && (
              <div style={{ fontSize: 12, color:'var(--cx-text-3)', marginBottom:8, lineHeight:1.5 }}>
                {entry.description}
              </div>
            )}
            <div style={{ fontSize: 12, color:'var(--cx-text-4)' }}>
              Started {new Date(entry.timer_started_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>

          {/* Actions */}
          <div style={{ padding: '10px 16px 14px', borderTop: '1px solid light-dark(#f1f5f9, #2a2e36)', display:'flex', gap:8 }}>
            <button
              onClick={stopTimer}
              disabled={stopping}
              style={{
                flex:1, padding:'8px 0', border:'none', borderRadius:8,
                background: stopping ? '#f1f5f9' : '#ef4444',
                color: stopping ? '#94a3b8' : '#fff',
                fontSize:12, fontWeight:700, cursor: stopping ? 'default' : 'pointer',
                transition:'background 0.15s',
              }}
            >
              {stopping ? 'Stopping…' : '⏹ Stop & Save'}
            </button>
            <button
              onClick={() => {
                setOpen(false);
                if (entry.project_id) navigate(`/projects/${entry.project_id}/timesheets`);
              }}
              style={{
                padding:'8px 12px', border:'1.5px solid var(--cx-line)', borderRadius:8,
                background:'var(--cx-surface)', color:'#374151',
                fontSize:12, fontWeight:600, cursor:'pointer',
                transition:'background 0.15s',
              }}
            >
              View
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
